'use client';

import { forwardRef, useEffect, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { ILocalizedParams } from '../../models/ICommon';
import LeftArrow from './svg/LeftArrow';
import MenuArrows from './svg/MenuArrows';
import { twMerge } from 'tailwind-merge';

import { localizedMonths } from '../../utils/date';
import { MenuSelect } from './MenuSelect';


interface Props {
  field: 'fromDate' | 'toDate';
  state: Partial<{ fromDate: Date; toDate: Date; }>;
  handleChange: (date: Date) => void;
  className?: string;
}

const DatePicker = forwardRef(function Inner({ locale = 'ar', className, state, field, handleChange }: ILocalizedParams<Props>, ref: any) {
  const months: string[] = localizedMonths[locale];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear - i);
  const isFrom = field === 'fromDate';

  return (
    <div dir='ltr' className={twMerge('absolute top-[105%] left-0 w-full z-10 h-[300px] rounded-bl-[42px] rounded-br-[42px]', className)} ref={ref}>

      <ReactDatePicker
      className='!rounded-[inherit]'
        renderCustomHeader={({
          date,
          changeMonth,
          changeYear,
        }) => (
          <div className='flex items-center justify-between w-full px-[8px] gap-x-[15px]'>
            <div className='flex items-center w-full justify-center gap-x-[8px] [&>*]:bg-primary-100 [&>*]:h-[40px] [&>*]:rounded-[6px] [&>*]:px-[16px] [&>*]:sm'>
              <MenuSelect
                selected={months[new Date(date).getMonth()]}
                options={months.map((o) => ({ title: o, value: o }))}
                onChange={(value) => changeMonth(months.indexOf(String(value)))}
                placeholder={months[new Date(date).getMonth()]}
                className={{ 
                  wrapper: 'gap-0 flex-[5] w-full select-sm dark:bg-night-300 dark:[&>*]:text-white', 
                  menu: { wrapper: 'grid-cols-1 bg-gray-100 z-10', item: 'sm' } 
                }}
              />

              <MenuSelect
                selected={years.find(y => y === new Date(date).getFullYear())}
                options={years.map((o) => ({ title: String(o), value: o }))}
                onChange={(value) => changeYear(years[years.indexOf(Number(value))])}
                placeholder={years[years.indexOf(currentYear)].toString()}
                className={{ 
                  wrapper: 'gap-0 flex-[4] w-full select-sm dark:bg-night-300 dark:[&>*]:text-white', 
                  menu: { wrapper: 'grid-cols-1 bg-gray-100 z-10', item: 'sm' } 
                }}
              />
            </div>
          </div>
        )}
        {
          ...(isFrom ? {
            selectsStart: true,
            maxDate: state['toDate'] ? new Date(state['toDate'])  : null
          } : {
            selectsEnd: true,
            minDate: state['fromDate'] ? new Date(state['fromDate'])  : null
          })
        }
        selected={state[field] ? new Date(state[field]!) :  null}
        startDate={isFrom ? null : (state['fromDate'] ? new Date(state['fromDate'])  : null)}
        endDate={isFrom ? (state['toDate'] ? new Date(state['toDate'])  : null) : null}
        // selectsRange
        inline
        fixedHeight
        onChange={handleChange}
        // dateFormat={'dd-MM-yyyy'}
      />
    </div>
  );
});


interface DatePickerCompProps {
  placeholder: string;
  field: 'fromDate' | 'toDate';
  state: Partial<{ fromDate: Date; toDate: Date; }>;
  handleDate: (field: 'fromDate' | 'toDate', date: Date) => void;
}


export default function DatePickerComp({ field, state, handleDate, placeholder }: DatePickerCompProps) {

  // const [localState, setLocalState] = useState({ fromDate, toDate });
  const [expanded, setExpanded] = useState(false);

  const pickerRef = useRef<any>();
  const containerRef = useRef<any>();

  const handleClick = (e: MouseEvent) => {
    const { current: picker } = pickerRef;
    const { current: container } = containerRef;
    if (
      (container?.contains(e.target) || container?.contains(picker)) &&
      (picker ? !picker.contains(e.target) : true)
    ) setExpanded(prevState => !prevState);
  }

  useEffect(() => {
    window.addEventListener('mousedown', handleClick);
    return () => {
      window.removeEventListener('mousedown', handleClick);
    }
  }, []);


  const getFormattedTitle = () => {
    let result = placeholder;
    const date = state[field];
    if (date) {
      const parsed = new Date(date);
      const day = parsed.getDate();
      const month = parsed.getMonth() + 1;
      const year = parsed.getFullYear();
      result = `${day}/${month}/${year}`;
    }
    return result;
  }

  return (
    <div ref={containerRef} className='py-[5px] dark:bg-night-300 dark:[&>*]:text-white rounded-[12px] gap-x-[30px] sm whitespace-nowrap cursor-pointer w-full px-[16px] relative bg-white dark:bg-night-100 flex items-center justify-between h-[48px]'>
      <span className='sm'>{getFormattedTitle()}</span>
      <MenuArrows className='w-[13px] h-[19px] dark:[&>path]:fill-white' />
      {expanded ? <DatePicker locale='ar'
        key={String(expanded)}
        ref={pickerRef}
        state={state}
        field={field}
        handleChange={(date: Date) => {
          handleDate(field,date);
            setExpanded(false)
        }} /> : null}
      {/* className={twMerge('hidden', expanded && 'block')} */}
    </div>
  )
}

